import { Pencil } from '@tamagui/lucide-icons';
import { useTranslation } from 'react-i18next';
import {
  contactRequestapi,
  KissanEventType,
  truncateName,
  updateFollowStatusApi,
  useAppPersistenceStore,
  useEventZStore,
  useProfileStore,
  UserProps,
} from '@ninjacart/nc-nk-store';
import {
  Avatar,
  Button,
  Card,
  CropIcon,
  LocationIcon,
  NinjacartLogo,
  Spacer,
  Stack,
  Text,
  UIBottomSheet,
  View,
  XStack,
  YStack,
  FarmerSVG, NavHeaderComponent, Tabs,
  FollowButton
} from '@ninjacart/nc-nk-ui';
import { ChevronDown, Plus } from '@tamagui/lucide-icons';
import React, { useEffect, useState, useRef } from 'react';
import { Pressable } from 'react-native';
import { TouchableOpacity } from 'react-native';
import { Linking, Platform } from 'react-native';
import { useRouter } from '@ninjacart/nc-nk-web-deps';
import { Paragraph, useTheme } from 'tamagui';

import { ReportProfile } from './reportProfile';
import { handleContactPermission } from '@ninjacart/nc-nk-web-deps';
import {
  useQueuedEventStore,
  addEventParam,
  INinjaEvent,
} from '@ninjacart/nc-events-sdk-v2';
import { getUserConnectionListApi } from '../api/workflow/userConnection';
import { IConnectionListPayload, IConnectionListResult } from '../api/workflow/workflowInterface';

interface IUserProfileCardProps {
  profileData: UserProps,
  name: string;
}


const UserProfileCard = ({ profileData, name }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { push } = useRouter();
  const userProfile = useProfileStore((state) => state.userProfile);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isFollowLoading, setIsFollowLoading] = useState(true);
  const hasUserLoggedIn = useProfileStore((state) => state.hasUserLoggedIn);
  const [bottomSheet, setBottomSheet] = useState<boolean>(false);
  const setProfileFollowersData = useProfileStore((state) => state.setProfileFollowersData);
  const contactIndex = useEventZStore((state) => state.contactIndex);
  const setContactIndex = useEventZStore((state) => state.setContactIndex);
  const [currentIndex, setCurrentIndex] = useState(0);
  const appLanguage = useAppPersistenceStore((state) => state.appLanguage);
  const enqueue = useRef(useQueuedEventStore((state) => state.enqueue)).current;

  const formatDate = (timestamp) => {
    if (!timestamp || isNaN(Date.parse(timestamp))) {
      return '';
    }

    const date = new Date(timestamp);
    const options: any = { day: 'numeric', month: 'long', year: 'numeric' };
    return date.toLocaleDateString('en-GB', options);
  };

  const formattedDate = formatDate(profileData?.user?.osCreatedAt);

  const getFormattedCropPreferences = (preferences) => {
    if (preferences && preferences.length > 0) {
      const cropNames = preferences.map((crop) => crop?.name);
      let result = cropNames.join(',');
      if (result?.length > 28) {
        const truncatedResult = result.substring(0, 28).split(',');
        const remainingCount = cropNames.length - truncatedResult.length;
        if (remainingCount > 0) {
          return `${truncatedResult.join(',')}...+${remainingCount} ${t('page.others')}`;
        }
        return `${truncatedResult.join(',')}...`;
      }
      return result;
    }
    return '';
  };

  const formattedCropPreferences = getFormattedCropPreferences(
    profileData?.userPreference?.cropPreferences
  );

  const gotoEdit = () => {
    handleEditProfileEvent();
    push('/editprofile');
  };

  const handleEditProfileEvent = () => {
    const profileEditSelectEvent: INinjaEvent = {
      name: KissanEventType.PROFILE_EDIT_SELECT,
      screenDetails: { screen: 'profile page' }
    };
    enqueue(profileEditSelectEvent);
  };

  const navigateToConnectionList = (activeTab) => {
    setProfileFollowersData({
      realmId: profileData?.user?.primaryRealm,
      userId: profileData?.user?.id,
    });
    push({
      pathname: '/connectionList',
      query: {
        Tab: activeTab,
      },
    });
  };

  const fetchConnectionData = async (): Promise<IConnectionListResult> => {
    try {
      const config: IConnectionListPayload = {
        realmId: userProfile?.realm?.id ?? '1',
        userId: userProfile?.user?.id ?? '1',
        type: 'FOLLOWED',
        offset: 0,
        count: 10,
        targetUserId: profileData?.user?.id,
      };
      const response: IConnectionListResult = await getUserConnectionListApi(config);
      setIsFollowLoading(false);
      if (response?.data?.length > 0) {
        setIsFollowing(true);
      }
      return response;
    } catch (error) {
      console.error('Error fetching Connection List Api', error);
      return { data: [] };
    }
  };

  useEffect(() => {
    if (hasUserLoggedIn()) {
      if (profileData?.user?.id === userProfile?.user?.id) return;
      fetchConnectionData();
    }
  }, []);

  const handleFollowEventClick = (event) => {
    const eventToEnqueue: INinjaEvent = {
      name: event,
      screenDetails: { screen: 'follow list' },
      eventParams: [addEventParam('followedUserId', profileData?.user?.id)],
    };
    enqueue(eventToEnqueue);
  };

  const handleButtonClick = async () => {
    if (isFollowing) {
      setBottomSheet(true);
      setCurrentIndex((prevIndex) => prevIndex + 1);
      handleFollowEventClick(KissanEventType.FOLLOW_BOTTOMSHEET_OPEN);
    } else {
      await updateConnectionStatus();
      handleFollowEventClick(KissanEventType.PROFILE_USER_FOLLOW);
    }
  };

  const handleUnfollow = async () => {
    handleFollowEventClick(KissanEventType.USER_UNFOLLOW_CLICK);
    setBottomSheet(false);
    await updateConnectionStatus();
  };

  const updateConnectionStatus = async () => {
    try {
      const prevState = isFollowing;
      setIsFollowing(!isFollowing);
      let config = {
        type: isFollowing ? 'unfollow' : 'follow',
        formData: {
          sourceObjectId: userProfile?.user?.id!,
          sourceObjectType: 'USER',
          destinationObjectId: profileData?.user?.id,
          destinationObjectType: 'USER',
          associationType: 'FOLLOWED',
        },
      };
      const response: boolean = await updateFollowStatusApi(config);
      await handleContactPermission();
      if (!response) {
        setIsFollowing(prevState);
      }
    } catch (error) {
      setIsFollowing(isFollowing);
      console.error('Error updating Connection Status', error);
    }
  };

  return (
    <>
      <Card
        shadowColor={'#000000'}
        shadowRadius={5}
        elevation={5}
        shadowOpacity={0.2}
        borderRadius={12}
        backgroundColor={theme['cardbackground']?.val}
        flex={1}
        minHeight={170}
      >
        <Stack paddingHorizontal="$4" paddingVertical="$3">
          <XStack alignItems="center">
            {profileData?.user?.mediaDetails?.media?.[0]?.mediaUrl ? (
              <Avatar circular size="$5">
                <Avatar.Image
                  accessibilityLabel="Profile Picture"
                  src={profileData?.user?.mediaDetails?.media?.[0]?.mediaUrl}
                />
              </Avatar>
            ) : (
              <FarmerSVG width={50} height={50} />
            )}

            <Spacer size="$4" />
            <YStack flex={1}>
              <XStack alignItems="center" marginBottom={6} justifyContent="space-between">
                <Text fontWeight="bold" numberOfLines={1} ellipsizeMode="tail" fontSize={appLanguage == 'ta' ? 12 : 14} color={'$usertextColor'}>
                {truncateName(
                    profileData?.user?.name ? profileData?.user?.name : name,
                    profileData?.user?.id == userProfile?.user?.id ? 15 : 12
                  )}
                </Text>

                {profileData?.user?.name && profileData?.user?.id === userProfile?.user?.id && (
                  <Stack
                    width={30}
                    height={30}
                    borderRadius={15}
                    borderColor={theme['primary']?.val}
                    borderWidth={1}
                    alignItems="center"
                    justifyContent="center"
                    onPress={gotoEdit}
                    testID="kisan_tId_editProfile_click"
                  >
                    <Pencil size={15} color={theme['primary']?.val} />
                  </Stack>
                )}

                {!isFollowLoading && (
                  <FollowButton
                    isFollowing={isFollowing}
                    isFollowLoading={isFollowLoading}
                    handleButtonClick={handleButtonClick}
                  />
                )}
              </XStack>
              <XStack>
                <Text
                  onPress={() => navigateToConnectionList('Followers')}
                  style={{
                    fontWeight: '400',
                    fontSize: 12,
                    textDecorationLine: 'underline',
                  }}
                  testID='kisan_tId_followers_click'
                >
                  {profileData?.user?.followedByCount ?? 0} {t('page.followers')}
                </Text>
                <Text
                  fontWeight={400}
                  fontSize={12}
                  marginHorizontal={8}
                >
                  |
                </Text>
                <Text
                  onPress={() => navigateToConnectionList('Following')}
                  style={{
                    fontWeight: '400',
                    fontSize: 12,
                    textDecorationLine: 'underline',
                  }}
                  testID='kisan_tId_following_click'
                >
                  {profileData?.user?.followedCount ?? 0} {t('page.following')}
                </Text>
              </XStack>
            </YStack>
          </XStack>
        </Stack>

        {/* Full-width horizontal line */}
        <XStack
          style={{
            borderBottomColor: theme['grey']?.val,
            borderBottomWidth: 0.5,
            marginVertical: 0.5,
          }}
        />

        <Stack paddingHorizontal="$4" paddingVertical="$3">
          <YStack space="$2" alignItems="flex-start">
            {(profileData?.user?.contactDetails?.address?.[0]?.district ||
              profileData?.user?.contactDetails?.address?.[0]?.state) && (
                <XStack alignItems="flex-start">
                  <LocationIcon width={20} height={20} />
                  <Spacer size="$2" />
                  <Text fontWeight="medium" fontSize="$4">
                    {`${profileData?.user?.contactDetails?.address?.[0]?.district ?? ''}${profileData?.user?.contactDetails?.address?.[0]?.district ? ', ' : ''}${profileData?.user?.contactDetails?.address?.[0]?.state ?? ''}`}
                  </Text>
                </XStack>
              )}

            {formattedCropPreferences && (
              <XStack alignItems="flex-start">
                <CropIcon width={20} height={20} />
                <Spacer size="$2" />
                <Text fontWeight="medium" fontSize="$4">
                  {formattedCropPreferences}
                </Text>
              </XStack>
            )}

            {formattedDate && (
              <XStack alignItems="flex-start">
                <NinjacartLogo width={20} height={20} />
                <Spacer size="$2" />
                <Text fontWeight="medium" fontSize="$4">
                  {t('page.joinedOn')} {formattedDate}
                </Text>
              </XStack>
            )}
          </YStack>
        </Stack>
      </Card>

      <UIBottomSheet
        open={bottomSheet}
        animation={'quick'}
        snapPoints={[40]}
        onOpenChange={setBottomSheet}
        children={<ReportProfile currentIndex={currentIndex} unFollowApiCall={handleUnfollow} />}
      />
    </>
  );
};

export default UserProfileCard;
